import React from "react";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { STAGES } from "../../config/offeringCatalog";
import {
  ADD_ON_CELL,
  GRID_COLUMNS,
  MATRIX_HEADER,
  NAME_CELL_SPAN,
  NOT_AVAILABLE_CELL,
  stageStyles,
} from "../../config/accentStyles";
import { useStageFeatureMatrix } from "../../hooks/useStageFeatureMatrix";
import MatrixToolbar from "../MatrixToolbar/MatrixToolbar";
import MatrixGroupBand from "../MatrixGroupBand/MatrixGroupBand";
import StageHeaderCell from "../StageHeaderCell/StageHeaderCell";
import OfferingRow from "../OfferingRow/OfferingRow";

const LegendSwatch: React.FC<{
  tone: string;
  symbol: string;
  label: string;
}> = ({ tone, symbol, label }) => (
  <span className="flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-300">
    <span
      aria-hidden="true"
      className={`flex h-5 w-[22px] items-center justify-center text-xs font-bold ${tone}`}
    >
      {symbol}
    </span>
    {label}
  </span>
);

const StageFeatureMatrix: React.FC = () => {
  const {
    groupBy,
    setGroupBy,
    category,
    setCategory,
    groups,
    visibleCount,
    totalCount,
    includedCounts,
    newCounts,
    addOnOnly,
    currentStage,
  } = useStageFeatureMatrix();

  const isFiltered = category !== "All";

  return (
    <MainPageWrapper
      fullWidth
      subtitle="CertainPath membership"
      title="Products & services"
    >
      <div className="space-y-4 py-4">
        <MatrixToolbar
          category={category}
          groupBy={groupBy}
          onCategoryChange={setCategory}
          onGroupByChange={setGroupBy}
        />

        {/*
          Phones (below md) get the stacked layout, which needs no sideways
          scroll. From md to 1340px the 788px grid may not fit beside the nav,
          so the wrapper scrolls horizontally; that also makes it a vertical
          scroll container (overflow-x: auto forces overflow-y to auto), which
          traps the sticky header. Above 1340px the grid fits, so the wrapper
          drops out of scroll-container duty and sticky resolves against the
          page instead.
        */}
        <div className="rounded-lg border border-gray-200 dark:border-slate-700 md:overflow-x-auto min-[1340px]:overflow-x-visible">
          <div className="md:min-w-[788px]">
            <div
              className={`grid ${GRID_COLUMNS} bg-white dark:bg-slate-800 ${MATRIX_HEADER}`}
            >
              <div
                className={`flex min-w-0 flex-col justify-end gap-2.5 px-4 py-3 md:px-5 ${NAME_CELL_SPAN}`}
              >
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-semibold leading-none text-gray-900 dark:text-white">
                    {visibleCount}
                  </span>
                  <span className="text-[12.5px] font-medium text-gray-900 dark:text-white">
                    {isFiltered ? `in ${category}` : "products & services"}
                  </span>
                </div>
                <div className="-mt-1.5 text-[11px] text-gray-500 dark:text-gray-400">
                  {isFiltered
                    ? `of ${totalCount} total`
                    : `Full catalog · ${includedCounts.Scale} included by Scale, ${addOnOnly} add-on only`}
                </div>
                <div className="flex flex-wrap items-center gap-4 border-t border-gray-100 pt-2.5 dark:border-slate-700/50">
                  <LegendSwatch
                    label="Included"
                    symbol="✓"
                    tone={`${stageStyles.Lead.tintBg} ${stageStyles.Lead.ink}`}
                  />
                  <LegendSwatch
                    label="Available Add-On"
                    symbol="+"
                    tone={ADD_ON_CELL}
                  />
                  <LegendSwatch
                    label="Not Available"
                    symbol="—"
                    tone={NOT_AVAILABLE_CELL}
                  />
                </div>
              </div>
              {STAGES.map((stage) => (
                <StageHeaderCell
                  includedCount={includedCounts[stage]}
                  isCurrent={currentStage === stage}
                  key={stage}
                  newCount={newCounts[stage]}
                  stage={stage}
                />
              ))}
            </div>

            {groups.map((group) => (
              <section key={group.key}>
                <MatrixGroupBand
                  intro={group.intro}
                  label={group.label}
                  sub={group.sub}
                />
                {group.offerings.map((offering) => (
                  <OfferingRow
                    key={offering.name}
                    offering={offering}
                    showCategory={groupBy === "stage"}
                  />
                ))}
              </section>
            ))}

            <p className="bg-white px-5 py-3.5 text-[12.5px] leading-relaxed text-gray-500 dark:bg-slate-800 dark:text-gray-400">
              Stages are cumulative — an offering included at an earlier stage
              stays included at every later stage.{" "}
              <span className="font-bold text-[#1F7A4D] dark:text-[#4ADE80]">
                +
              </span>{" "}
              marks an offering available as a paid add-on at that stage. Stage
              counts include ✓ items only.
            </p>
          </div>
        </div>
      </div>
    </MainPageWrapper>
  );
};

export default StageFeatureMatrix;
